import { GetUserToken, useAuth } from 'components/AuthProvider';
import { AdminBoxesQueryVariables, Prefecture_Enum } from 'generated/graphql';
import { GraphQLError } from 'graphql-request/dist/types';
import { getClientSdk } from 'graphql/client';
import { Box } from 'home/useBoxes';
import {
  PaginatedQueryConfig,
  TypedQueryFunction,
  usePaginatedQuery,
} from 'react-query';

export interface AdminBox extends Box {
  prefecture: Prefecture_Enum | null;
  plateNumber: string | null;
}

export type AdminBoxes = {
  boxes: AdminBox[];
  count: number;
};

export const AdminBoxesKey = 'adminBoxes';

const getAdminBoxes = (
  getUserToken: GetUserToken
): TypedQueryFunction<
  AdminBoxes,
  [typeof AdminBoxesKey, AdminBoxesQueryVariables]
> => async (key, vars) => {
  const token = await getUserToken();
  const { box, box_aggregate } = await getClientSdk(token).AdminBoxes(vars);

  const data: AdminBoxes = {
    boxes: box.map<AdminBox>((b) => ({
      id: b.id,
      imei: b.imei,
      name: b.name,
      coords: new google.maps.LatLng(
        b.coords.coordinates[1],
        b.coords.coordinates[0]
      ),
      address: b.address ?? '',
      postcode: b.postcode ?? '',
      prefecture: b.prefecture ?? null,
      prefectureName: b.prefectureByPrefecture?.name ?? '',
      plateNumber: b.plate_number ?? '',
    })),
    count: box_aggregate.aggregate?.count ?? 0,
  };

  return data;
};

export const useAdminBoxes = (
  vars: AdminBoxesQueryVariables,
  options?: PaginatedQueryConfig<AdminBoxes, GraphQLError[]>
) => {
  const { getUserToken } = useAuth();

  return usePaginatedQuery([AdminBoxesKey, vars], getAdminBoxes(getUserToken), {
    ...options,
    staleTime: 20000,
  });
};
